'use client';

import { useEffect, useRef, useState, PropsWithChildren, Children, ReactNode } from 'react';
import { cn } from '@fshr/shared-react';
import { ChevronBoldRight } from '../icon/chevron-bold-right';
import { ChevronBoldLeft } from '../icon/chevron-bold-left';
import BevSafeText from '../bev-safe-text';

interface CarouselItem {
	imageUrl: string;
	title: string;
	description: string;
}

interface CarouselProps {
	className?: string;
	disableButtons?: boolean;
	buttonLabels?: { previous: string; next: string };
	groupChildrensClassName?: string;
	items?: CarouselItem[];
}

export default function FullWidthCarousel({
	disableButtons = false,
	buttonLabels = { previous: 'Previous slide', next: 'Next slide' },
	className,
	children,
	groupChildrensClassName,
	items: carouselItems = [],
}: PropsWithChildren<CarouselProps>) {
	const ref = useRef<HTMLDivElement>(null);
	const [currentIndex, setCurrentIndex] = useState(1);
	const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
	const [itemWidth, setItemWidth] = useState(285);
	const [itemGap, setItemGap] = useState(15);

	// Base items: use carousel items if provided, otherwise fall back to children
	const baseItems: ReactNode[] =
		carouselItems.length > 0
			? (carouselItems as unknown as ReactNode[])
			: Children.toArray(children);
	// Extended items for infinite loop: [last, ...baseItems, first]
	const extendedItems: ReactNode[] =
		baseItems.length > 1
			? [baseItems[baseItems.length - 1], ...baseItems, baseItems[0]]
			: [...baseItems];
	const totalItems = extendedItems.length;

	const scrollToIndex = (index: number) => {
		setIsTransitionEnabled(true);
		setCurrentIndex(index);
	};

	const goToNext = () => {
		const nextIndex = currentIndex + 1;
		scrollToIndex(nextIndex);
	};

	const goToPrevious = () => {
		const prevIndex = currentIndex - 1;
		scrollToIndex(prevIndex);
	};

	// Handle transition end to correct clones for seamless loop
	const handleTransitionEnd = () => {
		if (baseItems.length <= 1) return;
		if (currentIndex === 0) {
			setIsTransitionEnabled(false);
			setCurrentIndex(baseItems.length);
			requestAnimationFrame(() => requestAnimationFrame(() => setIsTransitionEnabled(true)));
		} else if (currentIndex === baseItems.length + 1) {
			setIsTransitionEnabled(false);
			setCurrentIndex(1);
			requestAnimationFrame(() => requestAnimationFrame(() => setIsTransitionEnabled(true)));
		}
	};

	// Calculate dimensions and initialize scroll position in single effect
	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const calculateDimensionsAndScroll = () => {
			const grid = element.querySelector('.grid');
			if (!grid) return;

			// Get actual gap from CSS
			const computedGap = window.getComputedStyle(grid).gap;
			const gap = parseInt(computedGap || '15', 10);

			// Get actual item width from first child
			const firstItem = grid.firstElementChild as HTMLElement;
			if (!firstItem) return;

			const width = firstItem.clientWidth;

			// Update state for other functions to use
			setItemGap(gap);
			setItemWidth(width);

			// Immediately position to first real item (index 1) using transform model
			const initialIndex = baseItems.length > 1 ? 1 : 0;
			setIsTransitionEnabled(false);
			setCurrentIndex(initialIndex);
			requestAnimationFrame(() => setIsTransitionEnabled(true));
		};

		// Initial calculation and scroll
		calculateDimensionsAndScroll();

		// Recalculate on resize
		const handleResize = () => {
			calculateDimensionsAndScroll();
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [totalItems]);

	// No scroll listener needed for transform-based carousel

	// Don't render if no items or only one item
	if (baseItems.length <= 1) {
		return <div className="flex justify-center">{children}</div>;
	}

	const fullWidthClassNames = [
		'auto-cols-[285px] md:auto-cols-[570px] lg:auto-cols-[930px]',
		'no-scrollbar overflow-hidden px-[calc((100vw-285px)/2)] md:px-[calc((100vw-570px)/2)] lg:px-[calc((100vw-930px)/2)] w-screen',
	].join(' ');

	// Compute logical active base index to avoid opacity flicker across clones
	const baseLen = baseItems.length;
	const activeBaseIndex =
		baseLen > 0
			? currentIndex === 0
				? baseLen - 1
				: currentIndex === baseLen + 1
					? 0
					: currentIndex - 1
			: 0;

	return (
		<div className={cn(['relative', 'w-screen'])}>
			<div
				ref={ref}
				className={cn(fullWidthClassNames, className)}
				style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
			>
				<div
					className={cn(
						[
							'grid',
							'grid-flow-col',
							'gap-[30px]',
							'md:gap-300',
							'w-max',
							'my-0',
							'mx-auto',
							'will-change-transform',
						],
						groupChildrensClassName
					)}
					style={{
						transform: `translateX(-${currentIndex * (itemWidth + itemGap)}px)`,
						transition: isTransitionEnabled ? 'transform 300ms linear' : 'none',
					}}
					onTransitionEnd={handleTransitionEnd}
				>
					{extendedItems.map((item, index) => {
						// Map extended index to its corresponding base index
						const slideBaseIndex =
							baseLen > 0 ? (index === 0 ? baseLen - 1 : index === baseLen + 1 ? 0 : index - 1) : 0;
						const isActive = slideBaseIndex === activeBaseIndex;
						return (
							<div
								key={index}
								className={cn([
									'carousel-item w-full transition-opacity duration-300',
									isActive ? 'opacity-100' : 'opacity-60',
								])}
							>
								{carouselItems.length > 0 ? (
									<div className="h-auto w-[285px] md:w-[570px] lg:w-[930px] text-left flex flex-col">
										<img
											src={(item as any as CarouselItem).imageUrl}
											alt={
												(item as any as CarouselItem).title ||
												(item as any as CarouselItem).description
											}
											className="w-full object-cover aspect-[16/9]"
										/>
										<div className="py-200 flex flex-col gap-150 grow shrink">
											<h4 className="ty-c1">
												<BevSafeText>{(item as any as CarouselItem).title}</BevSafeText>
											</h4>
											<p className="ty-b2">
												<BevSafeText>{(item as any as CarouselItem).description}</BevSafeText>
											</p>
										</div>
									</div>
								) : (
									item
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* Navigation Arrows - positioned based on actual item dimensions */}
			{!disableButtons ? (
				<div
					className={cn([
						'flex',
						'absolute',
						'top-[calc(285px*9/32)]',
						'md:top-[calc(570px*9/32)]',
						'lg:top-[calc(930px*9/32)]',
						'-translate-y-1/2',
						'left-0',
						'right-0',
						'h-0',
						'flex-row',
						'justify-between',
					])}
				>
					<div
						className={cn([
							'w-300 flex justify-center items-center',
							'ml-[calc((100vw-285px)/2-30px)]',
							'md:ml-[calc((100vw-570px)/2-30px)]',
							'lg:ml-[calc((100vw-930px)/2-30px)]',
						])}
					>
						<button
							type="button"
							onClick={goToPrevious}
							className={cn(['w-200 h-200 relative z-10'])}
							aria-label={buttonLabels.previous}
						>
							<ChevronBoldLeft height={20} width={20} />
						</button>
					</div>

					<div
						className={cn([
							'w-300 flex justify-center items-center',
							'mr-[calc((100vw-285px)/2-30px)]',
							'md:mr-[calc((100vw-570px)/2-30px)]',
							'lg:mr-[calc((100vw-930px)/2-30px)]',
						])}
					>
						<button
							type="button"
							onClick={goToNext}
							className={cn(['w-200 h-200 relative z-10'])}
							aria-label={buttonLabels.next}
						>
							<ChevronBoldRight height={20} width={20} />
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
}
